class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.requestId = 0;
    this.searchTerm = '';
    this.resultCountMedia = window.matchMedia('(max-width: 749px)');
    this.onResultCountMediaChange = () => {
      if (this.isOpen && this.hasAttribute('results')) this.setLiveRegionResults();
    };

    this.setupEventListeners();
  }

  connectedCallback() {
    this.resultCountMedia.addEventListener('change', this.onResultCountMediaChange);
  }

  disconnectedCallback() {
    this.resultCountMedia.removeEventListener('change', this.onResultCountMediaChange);
    clearTimeout(this.focusOutTimeout);
    clearTimeout(this.liveRegionTimeout);
    this.close();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
      $(document).find('.search-popular-category').removeClass('hide');
      $(document).find('.no--results-found').remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      $(document).find('.search-popular-category').removeClass('hide');
      $(document).find('#predictive-search-results-groups-wrapper,#predictive-search-option-search-keywords').remove();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    clearTimeout(this.focusOutTimeout);
    this.focusOutTimeout = setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      const textParts = currentButtonText.split(previousTerm);
      if (textParts.length !== 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = textParts[0] + newTerm + textParts[1];
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll('[role="option"][id]')
    ).filter((element) => element.id && element.offsetParent !== null);
    if (!allVisibleElements.length) {
      selectedElement?.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', '');
      return;
    }

    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    const activeElementIndex = selectedElementIndex < 0
      ? (moveUp ? allVisibleElements.length - 1 : 0)
      : (selectedElementIndex + (moveUp ? -1 : 1) + allVisibleElements.length) % allVisibleElements.length;

    this.statusElement.textContent = "";
    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
    // S03a: focus stays in the field, so bring the highlighted option into view inside the scrolling popup.
    activeElement.scrollIntoView({ block: 'nearest' });
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    // A debounced input event may run after focus has left or this section was removed.
    if (!this.isConnected || !this.contains(document.activeElement)) return;
    $(document).find(".search-popup_content").removeClass("shadow--bottom shadow--top");
    const queryKey = searchTerm.toLowerCase();
    this.abortController.abort();
    this.abortController = new AbortController();
    const requestId = ++this.requestId;
    this.removeAttribute('results');
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    return fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (requestId !== this.requestId || searchTerm !== this.getQuery() || !this.isConnected || !this.contains(document.activeElement)) return;
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20 || requestId !== this.requestId) {
          // Code 20 means the call was aborted
          return;
        }
        // Keep ordinary full-search submission available when suggestions fail.
        this.removeAttribute('results');
        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    clearTimeout(this.liveRegionTimeout);
    this.liveRegionTimeout = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    let searchItem = parseInt($(this).find('#predictive-search-results').data('search-results'));
    if(searchItem > 0){
      $(document).find('.search-popular-category').addClass('hide');
      var thisElement = $(document).find('[data-popup-type="search"]').find(".popup--inner");
      var thisScrollTop = thisElement.scrollTop();
      var thisHeight = thisElement.height() + 2;
      var thisScrollHeight = thisElement.prop("scrollHeight");
      var resultLength = thisElement.find('#predictive-search-results-groups-wrapper').length;
      if(thisScrollHeight - thisScrollTop <= thisHeight) {
        $(document).find(".search-popup_content").removeClass("shadow--bottom");
      }else if (thisScrollTop == 0 && resultLength) {
        $(document).find(".search-popup_content").addClass("shadow--bottom");
      }else if (thisScrollTop == 0){
        $(document).find(".search-popup_content").removeClass("shadow--top");
      }
    }else{
      $(document).find('.search-popular-category').removeClass('hide');
    }
    $(document).find('.search-popup__form').addClass('is-results');
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const counts = this.querySelector('[data-predictive-search-live-region-count-value]');
    const limit = this.resultCountMedia.matches ? '2' : '3';
    const visibleCount = counts?.querySelector(`[data-predictive-search-count-limit="${limit}"]`);
    if (counts) this.setLiveRegionText((visibleCount || counts).textContent.trim());
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    //this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.requestId += 1;
    this.abortController.abort();
    clearTimeout(this.liveRegionTimeout);
    this.statusElement?.setAttribute('aria-hidden', 'true');
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

$(document)
.find('[data-popup-type="search"]')
.find(".popup--inner")
.scroll(function () {
  var thisElement = $(this);
  var thisScrollHeight = thisElement.prop("scrollHeight");
  var thisScrollTop = thisElement.scrollTop();
  var thisHeight = thisElement.height() + 2;
  var resultLength = thisElement.find('#predictive-search-results-groups-wrapper').length;
  if(thisScrollHeight - thisScrollTop <= thisHeight) {
    $(document).find(".search-popup_content").removeClass("shadow--bottom");
  }else if (thisScrollTop > 0 && resultLength) {
    $(document).find(".search-popup_content").addClass("shadow--bottom");
    $(document).find(".search-popup_content").addClass("shadow--top");
  }else if (thisScrollTop == 0 && resultLength){
    $(document).find(".search-popup_content").removeClass("shadow--top");
    $(document).find(".search-popup_content").addClass("shadow--bottom");
  }
});
